import { ArrowLeft, Loader2, Package, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../config/api';
import { useAppState } from '../../context/AppStateContext';
import { ui } from '../../styles/ui';

const CATEGORIES = ['Medicine', 'Food', 'Other'];
const UNITS = ['tablet', 'capsule', 'ml', 'bottle', 'vial', 'ampoule', 'tube', 'box', 'strip', 'sachet', 'kg', 'pcs', 'litters', 'meters'];

export const AddProductPage = () => {
  const navigate = useNavigate();
  const { apiCall, authToken, setProducts, setDataLoaded } = useAppState();
  const [formData, setFormData] = useState({ name: '', batchNo: '', description: '', category: 'Medicine', unit: 'tablet', quantity: '', price: '' });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitError) setSubmitError('');
  };

  const handleBack = () => navigate('/admin/products');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitError('');

    try {
      if (!formData.name || !formData.batchNo || !formData.description || !formData.quantity || !formData.price) {
        throw new Error('Please fill in all required fields');
      }
      if (isNaN(formData.quantity) || parseFloat(formData.quantity) <= 0) {
        throw new Error('Please enter a valid quantity');
      }
      if (isNaN(formData.price) || parseFloat(formData.price) <= 0) {
        throw new Error('Please enter a valid price');
      }

      const productData = { ...formData, quantity: parseInt(formData.quantity, 10), price: parseFloat(formData.price) };
      const response = await apiCall(API_CONFIG.endpoints.createProduct, 'POST', productData, true);

      if (response.success) {
        const productsResponse = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.products}?page=1&limit=10000`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        });
        const productsData = await productsResponse.json();
        if (productsResponse.ok && productsData.success) {
          setProducts(productsData.products || []);
          setDataLoaded((prev) => ({ ...prev, products: true }));
        }
        handleBack();
      } else {
        setSubmitError(response.message || 'Failed to create product');
      }
    } catch (err) {
      setSubmitError(err.message || 'Failed to create product');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back to Products
      </button>

      <div className={`${ui.card} p-5`}>
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-lg bg-emerald-600">
            <Package className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Add New Product</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Create a new product in the system</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={ui.label}>
              Product Name <span className="text-red-500">*</span>
            </label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} className={ui.input} placeholder="e.g., Megapen" required disabled={submitLoading} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={ui.label}>
                Batch Number <span className="text-red-500">*</span>
              </label>
              <input type="text" name="batchNo" value={formData.batchNo} onChange={handleInputChange} className={`${ui.input} font-mono`} placeholder="e.g., 89eeh2099" required disabled={submitLoading} />
            </div>
            <div>
              <label className={ui.label}>
                Category <span className="text-red-500">*</span>
              </label>
              <select name="category" value={formData.category} onChange={handleInputChange} className={ui.input} required disabled={submitLoading}>
                {CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={ui.label}>
              Description <span className="text-red-500">*</span>
            </label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} className={`${ui.input} resize-none`} placeholder="Product description" required disabled={submitLoading} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={ui.label}>
                Unit <span className="text-red-500">*</span>
              </label>
              <select name="unit" value={formData.unit} onChange={handleInputChange} className={ui.input} required disabled={submitLoading}>
                {UNITS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
              </select>
            </div>
            <div>
              <label className={ui.label}>
                Quantity <span className="text-red-500">*</span>
              </label>
              <input type="number" name="quantity" value={formData.quantity} onChange={handleInputChange} min="1" step="1" className={ui.input} placeholder="40" required disabled={submitLoading} />
            </div>
            <div>
              <label className={ui.label}>
                Price ($) <span className="text-red-500">*</span>
              </label>
              <input type="number" name="price" value={formData.price} onChange={handleInputChange} min="0" step="0.01" className={ui.input} placeholder="110.00" required disabled={submitLoading} />
            </div>
          </div>

          {submitError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg text-sm text-red-700 dark:text-red-300">
              {submitError}
            </div>
          )}

          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              The product will be assigned a unique barcode after creation and set to "pending" verification status.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5 sm:justify-end pt-2">
            <button type="button" onClick={handleBack} disabled={submitLoading} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-150 active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100">
              Cancel
            </button>
            <button type="submit" disabled={submitLoading} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-all duration-150 active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2">
              {submitLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating Product...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create Product
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
